import { Component, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { QueryDefinitionDto } from '../shared/dto/query-definition.dto';
import { TabComponent, TabsComponent } from 'src/app/shared/widgets/tabs';
import { DataQueryDto } from '../shared/dto/data-query.dto';
import { QueryToolViewResultsComponent } from './query-tool-view-results/query-tool-view-results.component';
import { ReportService } from '../shared/report-service';
import { ColumnDefinitionDto } from '../shared/dto/column-definition.dto';
import { SortDirectionEnum } from 'src/app/shared/search-expression/sort-expression.dto';
import { FieldValueOperatorEnum } from 'src/app/shared/search-expression/field-value-operator.enum';
import { TranslationService, UtilService } from 'src/app/shared';
import { BinaryOperatorEnum } from 'src/app/shared/search-expression/binary-operator.enum';
import { ActivatedRoute } from '@angular/router';
import { DatePeriodEnum } from 'src/app/shared/search-expression/date-period.enum';

@Component({
  selector: 'wl-report-query-tool',
  templateUrl: './report-query-tool.component.html',
  styleUrls: ['./report-query-tool.component.less'],
    standalone: false
})
export class ReportQueryToolComponent implements OnInit, OnChanges {
  queryDefinition: QueryDefinitionDto;
  dataQueryDto: DataQueryDto;
  @ViewChild(TabsComponent) tabsContainer: TabsComponent;
  @ViewChild(QueryToolViewResultsComponent) _queryToolViewResultsComponent: QueryToolViewResultsComponent;
  @ViewChild('viewResultsTab') viewResultsTab: TabComponent;
  @ViewChild('sqlTab') sqlTab: TabComponent;
  initComplete: boolean;
  availableFields: Array<ColumnDefinitionDto>;
  andString: string;
  orString: string;
  constructor(
    private _reportService: ReportService,
    private _utilService: UtilService,
    private _translationService: TranslationService,
    private _route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.queryDefinition = { Fields: [], SearchExpressions: [], SortExpressions: [] } as QueryDefinitionDto;
    this.dataQueryDto = {} as DataQueryDto;
    setTimeout(() => {
      this.andString = this._translationService.getInstantTitlecase('common.and');
      this.orString = this._translationService.getInstantTitlecase('common.or');
    }, 1000);

    this._route.params.subscribe(async (params) => {
      if (params['id']) {
        let queryId = parseInt(params['id'], 10);
        this.loadDataQuery(queryId).then((data) => {
          this.andString = this._translationService.getInstantTitlecase('common.and');
          this.orString = this._translationService.getInstantTitlecase('common.or');
          switch (params['command']) {
            case 'run':
              setTimeout(() => {
                this.selectViewResultsTab(this.viewResultsTab);
              }, 0);
              break;
            case 'sql':
              setTimeout(() => {
                this.selectTab(this.sqlTab);
              }, 0);
              break;
            default:
              if (this.dataQueryDto.IsRawSql) {
                this.selectTab(this.sqlTab);
              }
              break;
          }
        });
      }
    });
  }

  selectTab(tab: TabComponent) {
    this.tabsContainer.select(tab);
  }

  selectViewResultsTab(tab: TabComponent) {
    this.selectTab(tab);
    this._queryToolViewResultsComponent.runQuery();
  }

  async loadDataQuery(id: number) {
    await this._reportService.getDataQuery(id).then(async (dataQuery) => {
      this.queryDefinition = dataQuery.DefinitionDto;
      this.dataQueryDto = dataQuery;
      await this.viewChanged(this.dataQueryDto.DefinitionDto.ViewName);
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.queryDefinition) {
      if (this.queryDefinition.ViewName) {
        this.viewChanged(this.queryDefinition.ViewName);
        this._reportService.getFields(this.queryDefinition.ViewName).then((data) => {
          this.availableFields = data.filter((x) => x.Hide == false);
          this.initComplete = true;
        });
      } else {
        this.initComplete = true;
      }
    }
  }

  viewChanged(viewName: string) {
    this._reportService.getFields(viewName).then((data) => {
      if (this.initComplete) {
        this.queryDefinition.Fields.splice(0, this.queryDefinition.Fields.length);
      }
      this.availableFields = data.filter((x) => x.Hide == false);
      this.buildSql();
    });
  }

  public buildSql() {
    // don't build the sql
    if (this.dataQueryDto.IsRawSql) {
      return;
    }
    let selectString = this.buildSqlSelectClause();
    let whereClaus = this.buildSqlWhereClaus();
    let orderClaus = this.buildSqlOrderClaus();
    let fromString = `FROM ${this.queryDefinition.ViewName}`;

    this.dataQueryDto.Sql = `${selectString} ${fromString} ${whereClaus} ${orderClaus}`;
  }

  private buildSqlSelectClause() {
    let selectString = 'SELECT ';
    // Add Alias
    let fieldsWithAlias = this.queryDefinition.Fields.map((field) => {
      let replacementName = this.availableFields.find((x) => x.ColumnName == field)?.ReplacementName;
      replacementName = replacementName.replace("'", "''");
      return (field = `${field} as '${replacementName}'`);
    });

    selectString += fieldsWithAlias.join(',');
    return selectString;
  }

  private buildSqlOrderClaus() {
    if (this.queryDefinition.SortExpressions.length > 0) {
      let orderClaus = 'ORDER BY ';
      for (var i = 0; i < this.queryDefinition.SortExpressions.length; i++) {
        // Add the sort field
        orderClaus += `${this.queryDefinition.SortExpressions[i].SortField} `;
        // Add the direction
        orderClaus += `${this.queryDefinition.SortExpressions[i].SortDirection == SortDirectionEnum.Asc ? 'ASC' : 'DESC'}`;
        // Add a comma if not the last in array
        orderClaus += `${i == this.queryDefinition.SortExpressions.length - 1 ? '' : ','}`;
      }
      return orderClaus;
    } else {
      return '';
    }
  }

  private buildSqlWhereClaus() {
    if (this.queryDefinition.SearchExpressions.length > 0) {
      let whereClause = 'WHERE ( ';
      let lastFilterGroup = -1;
      // Sort by Filter Group
      this._utilService.sortByNumberAsc(this.queryDefinition.SearchExpressions, 'FilterGroup');

      for (var i = 0; i < this.queryDefinition.SearchExpressions.length; i++) {
        let searchExpression = this.queryDefinition.SearchExpressions[i];

        // Determine if start of new group
        if (i != 0 && lastFilterGroup != searchExpression.FilterGroup) {
          whereClause += `) ${this.getBinaryOperatorString(searchExpression.SetOperator)} (`;
        } else if (i != 0) {
          whereClause += ` ${this.getBinaryOperatorString(searchExpression.SetOperator)} `;
        }

        // Insert the expression
        let searchExpressionField = searchExpression.Field;
        if (searchExpression.FieldType == 'date') {
          searchExpressionField = 'CAST(' + searchExpressionField + ' AS DATE)';
        }

        whereClause += `${searchExpressionField} ${this.buildSearchExpression(
          searchExpression.Value,
          searchExpression.FieldValueOperator,
          searchExpression.FieldType
        )}`;

        // Insert Closing parentheses if it's last in array
        if (i == this.queryDefinition.SearchExpressions.length - 1) {
          whereClause += ')';
        }
        lastFilterGroup = this.queryDefinition.SearchExpressions[i].FilterGroup;
      }
      return whereClause;
    } else {
      return '';
    }
  }

  private getBinaryOperatorString(binaryOperator: BinaryOperatorEnum) {
    if (binaryOperator == BinaryOperatorEnum.And) {
      return this.andString;
    } else if (binaryOperator == BinaryOperatorEnum.Or) {
      return this.orString;
    }
  }

  private buildSearchExpression(value: string, valueOperator: FieldValueOperatorEnum, fieldType: string) {
    //In Period Date Operators
    if (fieldType == 'date' && valueOperator == FieldValueOperatorEnum.InPeriod && value != null) {
      return this.buildDatePeriodWhereClaus(value);
    }

    // Connvert to ISO string if date
    if (fieldType == 'date' && valueOperator != FieldValueOperatorEnum.InPeriod && value) {
      let date = new Date(value);
      value = date.toISOString().slice(0, 10);
    }

    value = value.replace(/'/g, "''");

    if (
      (fieldType == 'date' || fieldType == 'string') &&
      valueOperator != FieldValueOperatorEnum.Contains &&
      valueOperator != FieldValueOperatorEnum.DoesNotcontain
    ) {
      value = `'${value}'`;
    }

    switch (valueOperator) {
      case FieldValueOperatorEnum.Equals:
        return `= ${value}`;
      case FieldValueOperatorEnum.NotEqual:
        return `!= ${value}`;
      case FieldValueOperatorEnum.Contains:
        return `LIKE '%${value}%'`;
      case FieldValueOperatorEnum.DoesNotcontain:
        return `NOT LIKE '%${value}%'`;
      case FieldValueOperatorEnum.GreaterThan:
        return `> ${value}`;
      case FieldValueOperatorEnum.GreaterThanOrEqual:
        return `>= ${value}`;
      case FieldValueOperatorEnum.LessThan:
        return `< ${value}`;
      case FieldValueOperatorEnum.LessThanOrEqual:
        return `<= ${value}`;
    }
  }

  private buildDatePeriodWhereClaus(value: string) {
    switch (value.toString()) {
      case DatePeriodEnum.Last1Day.toString():
        return ` BETWEEN CAST(DATEADD(day,-1, GETDATE()) AS DATE) AND CAST(GETDATE() as DATE)`;
      case DatePeriodEnum.Last7Days.toString():
        return ` BETWEEN CAST(DATEADD(day,-7, GETDATE()) as DATE) AND CAST(GETDATE() as DATE)`;
      case DatePeriodEnum.Last30Days.toString():
        return ` BETWEEN CAST(DATEADD(day,-30, GETDATE()) as DATE) AND CAST(GETDATE() as DATE)`;
      case DatePeriodEnum.MonthToDate.toString():
        return ` BETWEEN CAST(DATEADD(month, DATEDIFF(month, 0, GETDATE()), 0) as DATE) AND CAST(GETDATE() as DATE)`;
      case DatePeriodEnum.PreviousMonth.toString():
        return ` BETWEEN CAST(DATEADD(MONTH, DATEDIFF(MONTH, 0, GETDATE())-1, 0)  as DATE) AND CAST(DATEADD(MONTH, DATEDIFF(MONTH, -1, GETDATE())-1, -1)  as DATE)`;
      case DatePeriodEnum.YearToDate.toString():
        return ` BETWEEN CAST(DATEFROMPARTS(YEAR(GETDATE()), 1, 1) as DATE) AND CAST(GETDATE() as DATE)`;
      case DatePeriodEnum.PreviousYear.toString():
        return ` BETWEEN CAST(DATEFROMPARTS(YEAR(GETDATE()) -1, 1, 1) as DATE) AND CAST(DATEFROMPARTS(YEAR(GETDATE()) -1, 12, 31) as DATE)`;
      case DatePeriodEnum.Next7Days.toString():
        return ` BETWEEN CAST(GETDATE() as DATE) AND CAST(DATEADD(day,7, GETDATE()) as DATE)`;
      case DatePeriodEnum.Next30Days.toString():
        return ` BETWEEN CAST(GETDATE() as DATE) AND CAST(DATEADD(day,30, GETDATE()) as DATE)`;
      case DatePeriodEnum.Next60Days.toString():
        return ` BETWEEN CAST(GETDATE() as DATE) AND CAST(DATEADD(day,60, GETDATE()) as DATE)`;
    }
  }
}
